import { useMemo } from "react";
import { addDays, endOfToday, format, startOfToday, subDays } from "date-fns";
import {
  useCreatePath,
  useGetList,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { getOpenDealStages } from "../deals/dealUtils";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { useDashboardScope } from "./useDashboardScope";

/** How long an account may go untouched before it counts as quiet. */
const QUIET_DAYS = 30;

/** How far ahead the "closing soon" tile looks. */
const CLOSING_SOON_DAYS = 30;

/** Tiles kept by the compact strip: the two task counts, which lead the list. */
const COMPACT_TILE_COUNT = 2;

type KpiTone = "neutral" | "critical" | "warn";

const toneClassName: Record<KpiTone, string> = {
  neutral: "text-foreground",
  critical: "text-destructive",
  warn: "text-amber-600 dark:text-amber-500",
};

type KpiTileProps = {
  label: string;
  value: string;
  tone: KpiTone;
  isPending: boolean;
  to: { pathname: string; search: string };
  /**
   * Why the figure is only a lower bound. Rendered next to the label rather
   * than hidden in a tooltip: a number that silently stops counting is worse
   * than one that says where it stopped.
   */
  note?: string;
};

const KpiTile = ({ label, value, tone, isPending, to, note }: KpiTileProps) => (
  <Link
    to={to}
    className="flex h-[58px] flex-col justify-center gap-1 bg-card px-3 py-2 transition-colors hover:bg-accent"
  >
    <span className="truncate text-xs text-muted-foreground">
      {label}
      {note ? <span className="ml-1">· {note}</span> : null}
    </span>
    {isPending ? (
      <Skeleton className="h-5 w-12" />
    ) : (
      <span
        className={cn(
          "text-lg font-semibold leading-none tabular-nums",
          toneClassName[tone],
        )}
      >
        {value}
      </span>
    )}
  </Link>
);

/**
 * How many figures the strip carries. `compact` keeps only the two task counts,
 * for the mobile home screen where six tiles would fill the first screenful;
 * the four queries it drops are not sent at all.
 */
export type DashboardKpisVariant = "full" | "compact";

/**
 * The six figures the dashboard opens on, all read through the current scope.
 * Counts come back as the `total` of a one-row page rather than as fetched rows.
 */
export const DashboardKpis = ({
  variant = "full",
}: {
  variant?: DashboardKpisVariant;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const createPath = useCreatePath();
  const { dealPipelineStatuses, dealStages } = useConfigurationContext();
  const { salesId, isTeam, isPending: isScopePending } = useDashboardScope();

  // Frozen for the lifetime of the widget: recomputing `new Date()` on every
  // render would change every query key and refetch the whole strip endlessly.
  const bounds = useMemo(() => {
    const now = new Date();
    return {
      todayStart: startOfToday().toISOString(),
      todayEnd: endOfToday().toISOString(),
      // `expected_closing_date` is a date column, so it is compared as a plain
      // day rather than as a timestamp.
      closingFrom: format(now, "yyyy-MM-dd"),
      closingTo: format(addDays(now, CLOSING_SOON_DAYS), "yyyy-MM-dd"),
      quietBefore: subDays(now, QUIET_DAYS).toISOString(),
    };
  }, []);

  const scopeFilter = useMemo(
    () => (isTeam ? {} : { sales_id: salesId }),
    [isTeam, salesId],
  );

  // The same open-stage set the Pipeline widget draws its bars from, so the two
  // figures on this screen always add up the same deals.
  const openStagesFilter = useMemo(
    () =>
      `(${getOpenDealStages(dealStages, dealPipelineStatuses)
        .map((stage) => stage.value)
        .join(",")})`,
    [dealPipelineStatuses, dealStages],
  );

  const overdueFilter = {
    ...scopeFilter,
    "done_date@is": null,
    "due_date@lt": bounds.todayStart,
  };
  const dueTodayFilter = {
    ...scopeFilter,
    "done_date@is": null,
    "due_date@gte": bounds.todayStart,
    "due_date@lte": bounds.todayEnd,
  };
  const openTasksFilter = { ...scopeFilter, "done_date@is": null };
  const openDealsFilter = {
    ...scopeFilter,
    "archived_at@is": null,
    "stage@in": openStagesFilter,
  };
  const closingSoonFilter = {
    ...openDealsFilter,
    "expected_closing_date@gte": bounds.closingFrom,
    "expected_closing_date@lte": bounds.closingTo,
  };
  const quietFilter = {
    ...scopeFilter,
    "last_activity_at@lt": bounds.quietBefore,
  };

  const isCompact = variant === "compact";
  const countQuery = { pagination: { page: 1, perPage: 1 } };
  const enabled = { enabled: !isScopePending };
  // The compact strip never renders these four, so it never asks for them.
  const enabledWhenFull = { enabled: !isScopePending && !isCompact };

  const overdue = useGetList(
    "tasks",
    { ...countQuery, filter: overdueFilter },
    enabled,
  );
  const dueToday = useGetList(
    "tasks",
    { ...countQuery, filter: dueTodayFilter },
    enabled,
  );
  const openTasks = useGetList(
    "tasks",
    { ...countQuery, filter: openTasksFilter },
    enabledWhenFull,
  );
  const openDeals = useGetList(
    "deals",
    { ...countQuery, filter: openDealsFilter },
    enabledWhenFull,
  );
  const closingSoon = useGetList(
    "deals",
    { ...countQuery, filter: closingSoonFilter },
    enabledWhenFull,
  );
  const quietAccounts = useGetList(
    "companies",
    { ...countQuery, filter: quietFilter },
    enabledWhenFull,
  );

  const formatCount = (total?: number) => (total ?? 0).toLocaleString(locale);
  const toList = (resource: string, filter: object) => ({
    pathname: createPath({ resource, type: "list" }),
    search: `filter=${JSON.stringify(filter)}`,
  });

  const tiles: Array<KpiTileProps & { key: string }> = [
    {
      key: "overdue",
      label: translate("crm.dashboard.kpi.overdue"),
      value: formatCount(overdue.total),
      tone: (overdue.total ? "critical" : "neutral") as KpiTone,
      isPending: overdue.isPending,
      to: toList("tasks", overdueFilter),
    },
    {
      key: "due_today",
      label: translate("crm.dashboard.kpi.due_today"),
      value: formatCount(dueToday.total),
      tone: "neutral" as KpiTone,
      isPending: dueToday.isPending,
      to: toList("tasks", dueTodayFilter),
    },
    {
      key: "open_tasks",
      label: translate("crm.dashboard.kpi.open_tasks"),
      value: formatCount(openTasks.total),
      tone: "neutral" as KpiTone,
      isPending: openTasks.isPending,
      to: toList("tasks", openTasksFilter),
    },
    {
      key: "open_pipeline",
      label: translate("crm.dashboard.kpi.open_pipeline"),
      value: formatCount(openDeals.total),
      tone: "neutral" as KpiTone,
      isPending: openDeals.isPending,
      to: toList("deals", openDealsFilter),
    },
    {
      key: "closing_soon",
      label: translate("crm.dashboard.kpi.closing_soon"),
      value: formatCount(closingSoon.total),
      tone: "neutral" as KpiTone,
      isPending: closingSoon.isPending,
      to: toList("deals", closingSoonFilter),
    },
    {
      key: "quiet_accounts",
      label: translate("crm.dashboard.kpi.quiet_accounts"),
      value: formatCount(quietAccounts.total),
      tone: (quietAccounts.total ? "warn" : "neutral") as KpiTone,
      isPending: quietAccounts.isPending,
      to: toList("companies", quietFilter),
    },
  ];

  // `gap-px` over the border colour draws the hairline dividers, so the tiles
  // read as one strip instead of a row of cards.
  return (
    <div
      className={cn(
        "grid grid-cols-2 gap-px overflow-hidden rounded-xl border bg-border",
        !isCompact && "md:grid-cols-3 lg:grid-cols-6",
      )}
    >
      {(isCompact ? tiles.slice(0, COMPACT_TILE_COUNT) : tiles).map(
        ({ key, ...tile }) => (
          <KpiTile key={key} {...tile} />
        ),
      )}
    </div>
  );
};
